/*
    The Performance Snapshot's six metrics (MPN-1530 §2).

    Pure arithmetic over chart data the page already fetched, kept out of the component
    so the parts that are easy to get subtly wrong -- blended rates, division by zero,
    an empty window -- can be tested without rendering anything.

    Every rate here is SUM(numerator) / SUM(denominator) over the whole window, never an
    average of per-day rates. Those differ, and the averaged version is wrong: a day with
    two clicks and a day with two thousand are not equally informative about what a click
    cost. §3.3 makes the same point about the table's total row.

    The sparklines plot the DAILY value of each metric, which is why performance-by-day returns
    spend, clicks and impressions rather than pre-divided rates. It is the same series
    the tab charts below use, so no extra query, as the ticket requires.
*/

export type SnapshotMetric = {
  key: string
  label: string
  value: string
  /** Daily values across the window, for the sparkline. Empty renders no line. */
  series: number[]
}

type CostRow = {
  day_start: string
  reporting_network: string | null
  spend_total: number | string
  clicks_total: number | string
  impression_total: number | string
}

type ConversionPoint = [number, number]

const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

/** A rate nobody can compute is shown as an em-less dash, per §3.3, not as 0 or NaN. */
export const NO_VALUE = "—"

export function money(value: number, digits = 2): string {
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`
}

export function count(value: number): string {
  return Math.round(value).toLocaleString()
}

export function ratio(value: number, digits = 2): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** Divide, or null when the denominator makes the answer meaningless rather than zero. */
export function safeDivide(numerator: number, denominator: number): number | null {
  if (!denominator) return null
  const result = numerator / denominator
  return Number.isFinite(result) ? result : null
}

/*
    `spend` is every channel's money, because a total that leaves some out is wrong to
    anyone reading it. `mediaSpend` excludes direct mail, and exists because CPC and CPM
    are media rates: a postcard has no clicks and a delivered card is not an impression, so
    including its cost in those numerators inflates a rate it was never part of
    (MPN-1706).
*/
export type DailyTotals = {
  day: string
  spend: number
  mediaSpend: number
  clicks: number
  impressions: number
}

// The reporting key for direct mail. Not a network, and the only channel here whose spend
// buys something other than impressions.
const DIRECT_MAIL = "direct_mail"

/**
 * Collapse the per-network cost rows into one row per day, in date order.
 *
 * Leading and trailing days with no delivery at all are dropped. The query zero-fills
 * the whole date filter window, so a campaign that started on 6 August returns twelve
 * empty days first when the filter says "last 30 days" — and a rate chart cannot plot
 * spend ÷ clicks on a day the campaign did not exist, so the line began a third of the
 * way across an otherwise blank axis. That is the gap Rachel reported; the days she
 * named had clicks, the empty stretch beside them did not.
 *
 * Only the ends are trimmed. A quiet day INSIDE the flight is real information and still
 * plots as a break, which is what the zero-fill was for.
 */
export function dailyTotals(rows: CostRow[] | undefined): DailyTotals[] {
  const byDay = new Map<string, DailyTotals>()
  for (const row of rows ?? []) {
    const day = row.day_start
    if (!day) continue
    const acc = byDay.get(day) ?? { day, spend: 0, mediaSpend: 0, clicks: 0, impressions: 0 }
    const spend = num(row.spend_total)
    acc.spend += spend
    if ((row as any).reporting_network !== DIRECT_MAIL) acc.mediaSpend += spend
    acc.clicks += num(row.clicks_total)
    acc.impressions += num(row.impression_total)
    byDay.set(day, acc)
  }
  const days = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day))

  // "No delivery" means nothing happened at all, not "nobody clicked". A day with
  // impressions and no clicks is a real day the campaign ran and must survive.
  const delivered = (d: DailyTotals) => d.spend > 0 || d.impressions > 0 || d.clicks > 0
  const first = days.findIndex(delivered)
  if (first === -1) return []

  let last = days.length - 1
  while (last > first && !delivered(days[last])) last--

  return days.slice(first, last + 1)
}

export type WindowTotals = {
  /** Every channel's money, including direct mail. */
  spend: number
  /** Spend on bidded media only, the numerator for CPC and CPM. */
  mediaSpend: number
  clicks: number
  impressions: number
  engagements: number
  conversions: number
  /** Current-state, deliberately NOT windowed. See §5.2. */
  matched: number
  /** The list we uploaded, and the denominator every per-user figure divides by. */
  selected: number
  /** Daily conversion values, for the sparkline. */
  conversionSeries: number[]
  /** Trimmed of the current part-day. The denominator side of every rate. */
  days: DailyTotals[]
  /** The same days with today kept, which is what `spend` is totalled over. */
  moneyDays: DailyTotals[]
}

/**
 * Every number the snapshot and the Conversions KPI cards share, computed once.
 *
 * Both surfaces show CPC and CPM for the same window. Computing them in two places is
 * how they end up disagreeing after someone edits one, so they read from here.
 */
export function windowTotals(charts: any): WindowTotals {
  const days = dailyTotals(charts?.["performance-by-day"])

  /*
      Money is totalled over the untrimmed series (MPN-2001).

      `days` has the current part-day removed, which is right for CPC and CPM and wrong
      for a total: the Spend tile read $990.73 while Spend By Network, Spend By Ad Group
      and the server's own Total Spend to Date all read $1,004.36 for the same window.
      Three server-side figures agreed; the odd one out was this one.

      Falls back to the trimmed series when the key is absent, so a caller handing in a
      raw payload (tests, and anything not routed through ChartSerializer) still totals
      something sensible rather than zero.
  */
  const moneyDays = charts?.["performance-by-day-all"]
    ? dailyTotals(charts["performance-by-day-all"])
    : days

  /*
      The conversion series arrives already shaped for Apex by ChartSerializer:
      [{ name, data: [[epochMs, value], ...] }]. Read the values off it rather than
      refetching, which is exactly what §2 means by reusing the tab's series.
  */
  const conversionPoints: ConversionPoint[] = charts?.["conversions-by-day"]?.[0]?.data ?? []
  const conversionSeries = conversionPoints.map(([, value]) => num(value))

  return {
    days,
    // The sparkline under the Spend tile is the tile's own series, so it runs to the same
    // day the number does. A tile whose total ends a day after its line is its own puzzle.
    moneyDays,
    spend: moneyDays.reduce((sum, d) => sum + d.spend, 0),
    // Rates keep the trimmed series: an hour of delivery makes a CPM read three times
    // normal, which is the whole reason the trim exists.
    mediaSpend: days.reduce((sum, d) => sum + d.mediaSpend, 0),
    clicks: days.reduce((sum, d) => sum + d.clicks, 0),
    impressions: days.reduce((sum, d) => sum + d.impressions, 0),
    // Engagements have no performance-by-day column; the HUD's own figure is authoritative.
    engagements: num(charts?.["conversions-hud"]?.engagement_count),
    conversionSeries,
    conversions: conversionSeries.reduce((sum, v) => sum + v, 0),
    /*
        Matched is a current-state count, not a windowed one (§5.2), so the per-student
        metrics are "this window's spend against the students we actually reached". A
        windowed denominator would make the ratio compare two different populations.
    */
    matched: num(charts?.["audience-network-counts"]?.matched),
    /*
        Per-user figures divide by SELECTED, the whole list we uploaded, not by
        Matched (Rachel and Megan, 27 Aug: "overall cost / overall list size").

        Matched flatters the numbers. It is the best single network's count, so it
        is always the smaller denominator and every per-user figure reads higher
        against it -- on Atlantic Cape, Selected 5,362 against Matched 2,000, a
        2.7x difference in the same metric. Selected is also the number Ops can
        state without a footnote: it is what we chose to target, whereas Matched is
        a floor derived from counts the networks return without identities.

        Cost per person we tried to reach is the question being asked. Matched
        stays on the Audience tab, per network, where it answers a different one:
        how well did each network find them.
    */
    selected: num(charts?.["audience-network-counts"]?.selected),
  }
}

export function buildSnapshot(charts: any): SnapshotMetric[] {
  const {
    days, moneyDays, spend, mediaSpend, clicks, impressions, conversions, conversionSeries,
    selected,
  } = windowTotals(charts)

  // Media rates use media spend. Blending a postcard's cost into CPM puts a rate of roughly
  // $900 per thousand next to CTV's $51 and makes the number useless; the money itself still
  // reconciles, because the Spend tile below counts every channel.
  const cpc = safeDivide(mediaSpend, clicks)
  const cpm = safeDivide(mediaSpend * 1000, impressions)
  const spendPerStudent = safeDivide(spend, selected)
  const impressionsPerStudent = safeDivide(impressions, selected)

  return [
    {
      key: "spend",
      label: "Spend",
      value: money(spend),
      // The tile's own series, so the line ends on the day the number counts up to.
      series: moneyDays.map((d) => d.spend),
    },
    {
      key: "conversions",
      label: "Conversions",
      value: count(conversions),
      series: conversionSeries,
    },
    {
      key: "cpc",
      label: "CPC",
      value: cpc === null ? NO_VALUE : money(cpc),
      series: days.map((d) => safeDivide(d.spend, d.clicks) ?? 0),
    },
    {
      key: "cpm",
      label: "CPM",
      value: cpm === null ? NO_VALUE : money(cpm),
      series: days.map((d) => safeDivide(d.spend * 1000, d.impressions) ?? 0),
    },
    {
      key: "spend-per-student",
      label: "Spend / User",
      value: spendPerStudent === null ? NO_VALUE : money(spendPerStudent),
      series: days.map((d) => safeDivide(d.spend, selected) ?? 0),
    },
    {
      key: "impressions-per-student",
      label: "Impressions / User",
      value: impressionsPerStudent === null ? NO_VALUE : ratio(impressionsPerStudent),
      series: days.map((d) => safeDivide(d.impressions, selected) ?? 0),
    },
  ]
}

/**
 * A sparkline path for `values`, drawn in a 100x30 viewBox.
 *
 * Hand-rolled rather than a chart instance: the Monitor page already disables Apex
 * animations because mounting many charts at once is what made it stutter, and six more
 * chart instances that live above every tab would be the same mistake. A polyline is
 * one string.
 *
 * Scaled to its OWN min and max, because these six metrics share no unit; a CPC of
 * $4 and 600,000 impressions on one scale would draw five flat lines and one spike.
 */
export function sparklinePoints(values: number[], width = 100, height = 30): string {
  const usable = values.filter((v) => Number.isFinite(v))
  if (usable.length === 0) return ""
  if (usable.length === 1) {
    // One point is a flat line rather than a dot, so the card still reads as a trend.
    return `0,${height / 2} ${width},${height / 2}`
  }

  const min = Math.min(...usable)
  const max = Math.max(...usable)
  const span = max - min
  const step = width / (usable.length - 1)

  return usable
    .map((value, index) => {
      const x = +(index * step).toFixed(2)
      // A flat series sits mid-box instead of dividing by zero and pinning to the top.
      const ratioOfSpan = span === 0 ? 0.5 : (value - min) / span
      // SVG y grows downward, so invert: the largest value gets the smallest y.
      const y = +(height - ratioOfSpan * height).toFixed(2)
      return `${x},${y}`
    })
    .join(" ")
}
